import type {
  DocumentAnswer,
  DocumentAnswerChange,
  DocumentAnswerKey,
  DocumentAnswers,
  TranscriptChunk,
} from '@/shared/api/transcriptor';

import type { DocumentTemplate, SectionOption } from '@/entities/templates';

import type { AnswerEdit, PendingAnswer } from '@/features/audioRecording';

import { isAllowedValue } from './blankValues';
import {
  answerKey,
  getAllowedValues,
  parseAnswerKey,
  getOptionSlots,
  hasSectionNote,
  joinAnswerValues,
  splitAnswerValues,
  type AiAnswer,
  type TemplateDraft,
} from './templateDraft';

// The note of a flexible section is an answer keyed like the text of a
// section without fields: a manual note is the clinician's, an AI one comes
// from the fill.
export type LiveDraftAnswers = Required<
  Pick<TemplateDraft, 'answers' | 'aiAnswers' | 'multiSelect'>
> & {
  // Answers the pinned template cannot place (unknown field, changed option
  // text or a value outside the strict list). They are not shown.
  unplaced: number;
};

type AnswerTarget =
  | { key: string; kind: 'section' }
  | { key: string; kind: 'note' }
  | { key: string; kind: 'statement'; option: SectionOption }
  | {
      key: string;
      kind: 'slot';
      option: SectionOption;
      allowed: string[];
    };

const findTarget = (
  template: DocumentTemplate,
  { sectionCode, optionIndex, slot }: DocumentAnswerKey,
): AnswerTarget | null => {
  const section = template.sections.find((item) => item.Code === sectionCode);
  if (!section) {
    return null;
  }
  if (optionIndex < 0) {
    if (slot) {
      return null;
    }
    if (!section.Options.length) {
      return { key: answerKey(section.Code), kind: 'section' };
    }
    return hasSectionNote(section)
      ? { key: answerKey(section.Code), kind: 'note' }
      : null;
  }
  const option = section.Options[optionIndex];
  if (!option) {
    return null;
  }
  if (!slot) {
    return getOptionSlots(option).length
      ? null
      : {
          key: answerKey(section.Code, optionIndex),
          kind: 'statement',
          option,
        };
  }
  const allowed = getAllowedValues(option, slot);
  return allowed
    ? {
        key: answerKey(section.Code, optionIndex, slot),
        kind: 'slot',
        option,
        allowed,
      }
    : null;
};

// `null` means the answer cannot be shown in this template.
const readValues = (target: AnswerTarget, answer: DocumentAnswer) => {
  if (target.kind === 'section' || target.kind === 'note') {
    return [answer.text];
  }
  if (target.kind === 'statement') {
    return answer.values.length ? [target.option.Text] : [];
  }
  if (!target.allowed.length) {
    return [answer.text];
  }
  const values = answer.values.filter((value) =>
    isAllowedValue(target.allowed, value),
  );
  return values.length < answer.values.length ? null : values;
};

const getEvidenceCitation = (
  indexes: number[],
  chunks: TranscriptChunk[],
): AiAnswer['citation'] => {
  const evidence = indexes.flatMap((index) =>
    chunks[index] ? [chunks[index]] : [],
  );
  return evidence.length
    ? {
        start: Math.min(...evidence.map((chunk) => chunk.start)),
        end: Math.max(...evidence.map((chunk) => chunk.end)),
      }
    : undefined;
};

const EMPTY_DRAFT_ANSWERS: LiveDraftAnswers = {
  answers: {},
  aiAnswers: {},
  multiSelect: {},
  unplaced: 0,
};

const placeAnswer = (
  draft: LiveDraftAnswers,
  answer: DocumentAnswer,
  template: DocumentTemplate,
  chunks: TranscriptChunk[],
): LiveDraftAnswers => {
  const target = findTarget(template, answer.key);
  const drifted =
    target &&
    target.kind !== 'section' &&
    target.kind !== 'note' &&
    !!answer.optionText &&
    answer.optionText !== target.option.Text;
  const values = target && !drifted ? readValues(target, answer) : null;
  if (!target || !values) {
    return { ...draft, unplaced: draft.unplaced + 1 };
  }
  const multiSelect =
    values.length > 1
      ? { ...draft.multiSelect, [target.key]: true as const }
      : draft.multiSelect;
  const value = answer.cleared ? '' : joinAnswerValues(values);
  if (answer.source === 'manual') {
    return {
      ...draft,
      multiSelect,
      answers: { ...draft.answers, [target.key]: value },
    };
  }
  if (!value.trim()) {
    return draft;
  }
  const citation = getEvidenceCitation(answer.evidenceChunkIndexes, chunks);
  return {
    ...draft,
    multiSelect,
    aiAnswers: {
      ...draft.aiAnswers,
      [target.key]: citation ? { value, citation } : { value },
    },
  };
};

// Maps the server snapshot onto the working-copy representation. Manual
// answers (including an explicit clear) go to `answers`, AI answers to
// `aiAnswers`, so the existing editor rules (manual wins) apply unchanged.
export const documentAnswersToDraft = ({
  template,
  snapshot,
  chunks = [],
}: {
  template: DocumentTemplate;
  snapshot: DocumentAnswers | null | undefined;
  chunks?: TranscriptChunk[];
}): LiveDraftAnswers => {
  if (!snapshot) {
    return EMPTY_DRAFT_ANSWERS;
  }
  const placed = snapshot.answers.reduce(
    (draft, answer) => placeAnswer(draft, answer, template, chunks),
    EMPTY_DRAFT_ANSWERS,
  );
  return snapshot.notes.reduce((draft, note) => {
    const section = template.sections.find(
      (item) => item.Code === note.sectionCode,
    );
    if (!section || !hasSectionNote(section)) {
      return note.text.trim()
        ? { ...draft, unplaced: draft.unplaced + 1 }
        : draft;
    }
    const key = answerKey(section.Code);
    if (note.source === 'manual') {
      return { ...draft, answers: { ...draft.answers, [key]: note.text } };
    }
    return note.text.trim()
      ? {
          ...draft,
          aiAnswers: { ...draft.aiAnswers, [key]: { value: note.text } },
        }
      : draft;
  }, placed);
};

// The working copy keys answers as scribe does; only a key the template can
// place is sent.
export const answerKeyToDocumentKey = (
  template: DocumentTemplate,
  key: string,
): DocumentAnswerKey | null => {
  const documentKey = parseAnswerKey(key);
  return findTarget(template, documentKey) ? documentKey : null;
};

// An empty value is the clinician's explicit clear: AI must not refill it.
export const draftAnswerToChange = ({
  template,
  key,
  value,
}: {
  template: DocumentTemplate;
  key: string;
  value: string;
}): DocumentAnswerChange | null => {
  const documentKey = answerKeyToDocumentKey(template, key);
  if (!documentKey) {
    return null;
  }
  const target = findTarget(template, documentKey);
  if (!target) {
    return null;
  }
  if (target.kind === 'statement') {
    return value
      ? { key: documentKey, values: [target.option.Text] }
      : { key: documentKey, clear: true };
  }
  if (target.kind === 'slot' && target.allowed.length) {
    const values = splitAnswerValues(value);
    return values.length
      ? { key: documentKey, values }
      : { key: documentKey, clear: true };
  }
  return value.trim()
    ? { key: documentKey, text: value }
    : { key: documentKey, clear: true };
};

export const draftAnswerRelease = ({
  template,
  key,
}: {
  template: DocumentTemplate;
  key: string;
}): DocumentAnswerChange | null => {
  const documentKey = answerKeyToDocumentKey(template, key);
  return documentKey && findTarget(template, documentKey)
    ? { key: documentKey, release: true }
    : null;
};

export const toAnswerEdit = (params: {
  template: DocumentTemplate;
  key: string;
  value: string;
}): AnswerEdit | null => {
  const change = draftAnswerToChange(params);
  return (
    change && {
      key: params.key,
      pending: { value: params.value },
      change,
      immediate: change.text === undefined,
    }
  );
};

export const toReleaseEdit = (params: {
  template: DocumentTemplate;
  key: string;
}): AnswerEdit | null => {
  const change = draftAnswerRelease(params);
  return (
    change && {
      key: params.key,
      pending: { release: true },
      change,
      immediate: true,
    }
  );
};

// Edits in flight stay visible until the server accepts or rejects them.
export const applyPendingAnswers = (
  draft: LiveDraftAnswers,
  pending: Record<string, PendingAnswer>,
): LiveDraftAnswers => ({
  ...draft,
  answers: Object.entries(pending).reduce((answers, [key, change]) => {
    if ('release' in change) {
      return Object.fromEntries(
        Object.entries(answers).filter(([item]) => item !== key),
      );
    }
    return 'value' in change ? { ...answers, [key]: change.value } : answers;
  }, draft.answers),
});
